import React from 'react';
import { ExtraScopeEnum } from '@koob/enums';
import TripExperienceFrame from '../TripExperienceFrame';
import TripExperienceFrameHeader from '../TripExperienceFrameHeader';
import { getFrameLocations } from '../../logic/versionCompare';

interface TripVersionCompareFrameProps {
  frame: any;
  days: any[];
  experience: any;
  factsheetUrl?: string;
  translation: (key: string, options?: Record<string, any>) => string;
  // The global services band that closes the frame.
  renderGlobalServices: (globalExtras: any[]) => React.ReactNode;
  children: React.ReactNode;
}

// One experience of a version, read-only: its header, its days, and the
// global services that close it.
export default function TripVersionCompareFrame({
  frame,
  days,
  experience,
  factsheetUrl,
  translation: t,
  renderGlobalServices,
  children,
}: TripVersionCompareFrameProps) {
  const { dayIndices } = frame;
  const firstDayIndex = dayIndices[0];
  const lastDayIndex = dayIndices[dayIndices.length - 1];

  const { start, end } = getFrameLocations(experience, dayIndices.length);
  const itineraryLabel =
    start && end && start !== end
      ? t('compose.frame.itinerary', { from: start, to: end })
      : (start ?? end ?? null);

  // A global service is filed on one day of its experience — the first one as
  // a rule, but older trips carry some on the others, so the whole frame is
  // swept exactly as the live band does.
  const globalExtras = dayIndices.flatMap((index: number) =>
    (days?.[index]?.selectedExtras ?? []).filter(
      (extra: any) => extra?.scope === ExtraScopeEnum.GLOBAL,
    ),
  );

  return (
    <TripExperienceFrame
      globalServices={renderGlobalServices(globalExtras)}
      header={
        <TripExperienceFrameHeader
          name={experience?.name ?? ''}
          factsheetUrl={factsheetUrl}
          factsheetLabel={t('compose.frame.openFactsheet')}
          imageUrl={
            experience?.imageUrl ??
            experience?.pictures?.[0]?.imageUrl ??
            '/img/hotel-image-placeholder.png'
          }
          dayRangeLabel={
            firstDayIndex === lastDayIndex
              ? t('compose.frame.singleDay', { day: firstDayIndex + 1 })
              : t('compose.frame.dayRange', {
                  start: firstDayIndex + 1,
                  end: lastDayIndex + 1,
                })
          }
          itineraryLabel={itineraryLabel}
        />
      }
    >
      {children}
    </TripExperienceFrame>
  );
}
